import React, { forwardRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import * as Yup from "yup";

import { TextField } from "@material-ui/core";
import { ButtonsWrapper, StyledForm } from "./styled";

import { getRequiredField } from "helpers/formValidations";
import { COLLECT_PERIODS_CONFIG, COLLECT_PERIODS_PHRASE } from "./config";

import RegularButton from "components/CustomButtons/Button";
import AutocompleteControlled from "components/AutocompleteControlled/AutocompleteControlled";
import { fetchNewCollectPeriod, fetchEditCollectPeriod } from "redux/actions/buildings/collectPeriods/collectPeriodsActions";

const CollectPeriodsSchema = Yup.object({
  [COLLECT_PERIODS_CONFIG.name.key]: Yup.string().required(
    getRequiredField(COLLECT_PERIODS_CONFIG.name.label)
  ),
  [COLLECT_PERIODS_CONFIG.type.key]: Yup.object().required(
    getRequiredField(COLLECT_PERIODS_CONFIG.type.label)
  ),
  [COLLECT_PERIODS_CONFIG.cutDay.key]: Yup.string().required(
    getRequiredField(COLLECT_PERIODS_CONFIG.cutDay.label)
  ),
  [COLLECT_PERIODS_CONFIG.payDay.key]: Yup.string().required(
    getRequiredField(COLLECT_PERIODS_CONFIG.payDay.label)
  ),
});

export const CollectPeriodsForm = forwardRef((props) => {
  const dispatch = useDispatch();
  const { handleClose, stationSelected } = props;
  const { isAddMode, deleteMode, types, editCollectPeriod } = useSelector(
    (state) => state.collectPeriods
  );

  const handleCloseForm = () => {
    handleClose();
    reset();
  };

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(CollectPeriodsSchema),
  });

  const onSubmit = React.useCallback(
    (formData) => {
      const body = {
        name: formData[COLLECT_PERIODS_CONFIG.name.key],
        idStation: stationSelected.id,
        type: formData[COLLECT_PERIODS_CONFIG.type.key].value,
        cutDay: formData[COLLECT_PERIODS_CONFIG.cutDay.key],
        payDay: formData[COLLECT_PERIODS_CONFIG.payDay.key],
      };

      if (isAddMode) {
        dispatch(fetchNewCollectPeriod(body));
      } else {
        dispatch(fetchEditCollectPeriod(body, editCollectPeriod.id));
      }
      handleCloseForm();
    },
    [reset]
  );

  useEffect(() => {
    if (editCollectPeriod) {
      const editType = types.find(
        (type) => type.value === editCollectPeriod.type
      );

      setValue(
        COLLECT_PERIODS_CONFIG.name.key,
        editCollectPeriod[COLLECT_PERIODS_CONFIG.name.key]
      );
      setValue(
        COLLECT_PERIODS_CONFIG.cutDay.key,
        editCollectPeriod[COLLECT_PERIODS_CONFIG.cutDay.key]
      );
      setValue(
        COLLECT_PERIODS_CONFIG.payDay.key,
        editCollectPeriod[COLLECT_PERIODS_CONFIG.payDay.key]
      );

      setValue(COLLECT_PERIODS_CONFIG.type.key, editType);
    }
  }, [editCollectPeriod]);

  useEffect(() => {
    if (isAddMode) {
      reset();
    }
  }, [isAddMode]);

  return (
    <>
      <StyledForm component="form" fullWidth onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          type="text"
          label={COLLECT_PERIODS_CONFIG.name.label}
          variant="outlined"
          error={!!errors[COLLECT_PERIODS_CONFIG.name.key]}
          helperText={errors[COLLECT_PERIODS_CONFIG.name.key]?.message}
          {...register(COLLECT_PERIODS_CONFIG.name.key)}
          size="small"
        />
        <AutocompleteControlled
          name={COLLECT_PERIODS_CONFIG.type.key}
          label={COLLECT_PERIODS_CONFIG.type.label}
          options={types}
          control={control}
        />

        <TextField
          fullWidth
          type="number"
          label={COLLECT_PERIODS_CONFIG.cutDay.label}
          variant="outlined"
          error={!!errors[COLLECT_PERIODS_CONFIG.cutDay.key]}
          helperText={errors[COLLECT_PERIODS_CONFIG.cutDay.key]?.message}
          {...register(COLLECT_PERIODS_CONFIG.cutDay.key)}
          size="small"
        />

        <TextField
          fullWidth
          type="number"
          label={COLLECT_PERIODS_CONFIG.payDay.label}
          variant="outlined"
          error={!!errors[COLLECT_PERIODS_CONFIG.payDay.key]}
          helperText={errors[COLLECT_PERIODS_CONFIG.payDay.key]?.message}
          {...register(COLLECT_PERIODS_CONFIG.payDay.key)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        <ButtonsWrapper>
          <RegularButton
            variant="contained"
            color="danger"
            onClick={handleClose}
          >
            {COLLECT_PERIODS_PHRASE.modal.actions.cancel}
          </RegularButton>
          <RegularButton
            variant="contained"
            type="submit"
            color="info"
          >
            {isAddMode
              ? COLLECT_PERIODS_PHRASE.modal.actions.add
              : deleteMode
              ? COLLECT_PERIODS_PHRASE.modal.actions.delete
              : COLLECT_PERIODS_PHRASE.modal.actions.save}
          </RegularButton>
        </ButtonsWrapper>
      </StyledForm>
    </>
  );
});
